import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GripVertical, Info, Plus, Trash2 } from "lucide-react";
import { useDealStages } from "@/hooks/useDealStages";
import { cn } from "@/lib/utils";

interface DealStageManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

const STAGE_MANAGER_HELP = (
  <div className="space-y-3 text-xs leading-relaxed text-muted-foreground">
    <div>
      <p className="font-medium text-foreground">O que é o pipeline ponderado</p>
      <p className="mt-1">
        Soma dos valores das negociações <span className="font-medium text-foreground">abertas</span>, ajustados
        pela chance de fechamento de cada estágio. O pipeline bruto trata tudo como certo; o ponderado estima o
        que você tem maior chance de receber de fato.
      </p>
      <p className="mt-1.5">
        <span className="font-medium text-foreground">Fórmula:</span> valor × probabilidade ÷ 100
      </p>
      <p className="mt-1">
        <span className="font-medium text-foreground">Exemplo:</span> R$ 10.000 em Proposta (30%) contribui com
        R$ 3.000 no pipeline ponderado.
      </p>
      <p className="mt-1">
        Usado no painel de insights (ao lado do pipeline bruto), nos gráficos de previsão e no perfil comercial do
        lead.
      </p>
    </div>
    <p className="font-medium text-foreground">Para que serve cada campo</p>
    <ul className="space-y-2 list-none">
      <li>
        <span className="font-medium text-foreground">Nome</span> — para que serve: definir o título exibido na
        coluna do kanban de negociações.
      </li>
      <li>
        <span className="font-medium text-foreground">% (probabilidade)</span> — para que serve: estimar a chance
        de fechar negócios neste estágio (0 a 100). Ao mover uma negociação para o estágio, essa % é aplicada a
        ela e recalcula o valor ponderado automaticamente.
      </li>
      <li>
        <span className="font-medium text-foreground">Ganho</span> — para que serve: marcar o estágio de vitória
        (negócio fechado com sucesso). Negócios movidos para cá passam a status ganho. Mantenha apenas um estágio
        de ganho.
      </li>
      <li>
        <span className="font-medium text-foreground">Perdido</span> — para que serve: marcar o estágio de perda.
        Negócios movidos para cá passam a status perdido. Ganho e perdido não podem estar ativos no mesmo estágio.
      </li>
      <li>
        <span className="font-medium text-foreground">Cor</span> — para que serve: identificar visualmente a coluna
        no kanban (bolinha colorida ao lado do nome).
      </li>
      <li>
        <span className="font-medium text-foreground">Ordem na lista</span> — para que serve: definir a sequência
        das colunas no kanban, da esquerda para a direita.
      </li>
      <li>
        <span className="font-medium text-foreground">Excluir</span> — para que serve: remover um estágio que não
        é mais usado. Só é possível se não houver negociações vinculadas a ele.
      </li>
      <li>
        <span className="font-medium text-foreground">Novo estágio</span> — para que serve: criar uma nova etapa
        do funil. Informe o nome e a probabilidade inicial; o estágio é adicionado ao final do pipeline.
      </li>
    </ul>
  </div>
);

function DealStageManagerInfo({ className }: { className?: string }) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <Tooltip open={popoverOpen ? false : undefined} delayDuration={300}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Ajuda sobre os campos do gerenciador de estágios"
              className={cn(
                "inline-flex items-center justify-center rounded-sm p-1",
                "text-muted-foreground opacity-70 ring-offset-background transition-all",
                "hover:opacity-100 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                className,
              )}
            >
              <Info className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-[240px] text-xs">
          Clique para ver a ajuda dos campos
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="left"
        align="end"
        collisionPadding={16}
        className="w-[min(100vw-2rem,320px)] p-0 overflow-hidden"
      >
        <div
          className="max-h-[min(50vh,360px)] overflow-y-auto overscroll-contain p-4"
          onWheelCapture={(e) => e.stopPropagation()}
        >
          {STAGE_MANAGER_HELP}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DealStageManagerDialog({ open, onOpenChange, workspaceId }: DealStageManagerDialogProps) {
  const { stages, updateStage, createStage, deleteStage } = useDealStages(workspaceId);
  const [newName, setNewName] = useState("");
  const [newProb, setNewProb] = useState("50");

  const handleAdd = () => {
    if (!newName.trim()) return;
    const slug = newName.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const maxOrder = stages.reduce((m, s) => Math.max(m, s.order_index), -1);
    createStage.mutate({
      workspace_id: workspaceId,
      name: newName.trim(),
      slug: slug || `stage_${Date.now()}`,
      probability: parseInt(newProb) || 50,
      order_index: maxOrder + 1,
      color: "bg-blue-500",
    });
    setNewName("");
    setNewProb("50");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DealStageManagerInfo className="absolute right-11 top-4 z-10" />
        <DialogHeader className="space-y-0 pr-14">
          <DialogTitle className="text-left leading-snug">Gerenciar Estágios do Pipeline</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {stages.map(stage => (
            <div key={stage.id} className="flex items-center gap-2 border rounded-lg p-2">
              <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
              <div className={`w-3 h-3 rounded-full shrink-0 ${stage.color}`} />
              <Input
                className="h-8 flex-1 min-w-0"
                defaultValue={stage.name}
                aria-label="Nome do estágio"
                onBlur={e => { if (e.target.value !== stage.name) updateStage.mutate({ id: stage.id, name: e.target.value }); }}
              />
              <Input
                type="number"
                min={0}
                max={100}
                className="h-8 w-16"
                defaultValue={stage.probability}
                aria-label="Probabilidade de fechamento"
                onBlur={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v !== stage.probability) updateStage.mutate({ id: stage.id, probability: v }); }}
              />
              <span className="text-xs text-muted-foreground shrink-0">%</span>
              <div className="flex items-center gap-1 text-xs shrink-0">
                <Switch
                  checked={stage.is_closed_won}
                  onCheckedChange={v => updateStage.mutate({ id: stage.id, is_closed_won: v, is_closed_lost: v ? false : stage.is_closed_lost })}
                />
                <span>Ganho</span>
              </div>
              <div className="flex items-center gap-1 text-xs shrink-0">
                <Switch
                  checked={stage.is_closed_lost}
                  onCheckedChange={v => updateStage.mutate({ id: stage.id, is_closed_lost: v, is_closed_won: v ? false : stage.is_closed_won })}
                />
                <span>Perdido</span>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => deleteStage.mutate(stage.id)}>
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2 items-end border-t pt-3">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Novo estágio</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nome" className="h-8" />
            </div>
            <div className="w-20 space-y-1">
              <Label className="text-xs">Prob. %</Label>
              <Input type="number" min={0} max={100} value={newProb} onChange={e => setNewProb(e.target.value)} className="h-8" />
            </div>
            <Button size="sm" onClick={handleAdd} disabled={createStage.isPending}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
